import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTruckLoading, faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import purchaseOrderService from '../../services/purchaseOrderService';

const StartUnloadingForm = ({ isOpen, onClose, onSubmit, adviceId, poNumber, adviceNumber, remark = "", loading = false }) => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        loaded_truck_weight: '',
        remark: remark || '',
        internal_remark: '',
    });


    const [errors, setErrors] = useState({});
    const [adviceData, setAdviceData] = useState(null);
    const [fetching, setFetching] = useState(false);

    const lastFetchedIdRef = React.useRef(null);

    useEffect(() => {
        if (isOpen && adviceId) {
            // Prevent duplicate calls for the same ID
            if (lastFetchedIdRef.current === adviceId) return;
            lastFetchedIdRef.current = adviceId;

            const fetchData = async () => {
                setFetching(true);
                try {
                    const result = await purchaseOrderService.getPurchaseAdviceById(adviceId);
                    if (result.success) {
                        setAdviceData(result.data);
                        if (result.data.remark) {
                            setFormData(prev => ({ ...prev, remark: result.data.remark }));
                        }
                        if (result.data.internal_remark) {
                            setFormData(prev => ({ ...prev, internal_remark: result.data.internal_remark }));
                        }

                    }
                } catch (error) {
                    console.error('Failed to fetch advice details:', error);
                } finally {
                    setFetching(false);
                }
            };
            fetchData();
        } else {
            lastFetchedIdRef.current = null;
            setAdviceData(null);
        }
    }, [isOpen, adviceId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.loaded_truck_weight) newErrors.loaded_truck_weight = t('purchaseOrders.forms.startUnloadingForm.errors.finalWeightRequired');
        else if (isNaN(formData.loaded_truck_weight) || parseFloat(formData.loaded_truck_weight) <= 0) {
            newErrors.loaded_truck_weight = t('purchaseOrders.forms.startUnloadingForm.errors.invalidWeight');
        }



        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        const payload = {
            loaded_truck_weight: parseFloat(formData.loaded_truck_weight),
            remark: formData.remark,
            internal_remark: formData.internal_remark || '',
        };
        onSubmit(payload);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 shadow-xl" style={{ backgroundColor: colorHelpers.withOpacity('#000000', 0.5) }}>
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col relative overflow-hidden" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                {(loading || fetching) && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.OVERLAY, 0.6) }}>
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: UI_COLORS.BRAND.PRIMARY }}></div>
                    </div>
                )}

                <div className="flex items-center justify-between p-6 border-b flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('purchaseOrders.forms.startUnloadingForm.title')}</h2>
                    <button onClick={onClose} className="p-2 rounded-lg transition-colors hover:bg-gray-100" style={{ color: UI_COLORS.TEXT.MUTED }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto custom-scrollbar max-h-[75vh]">
                    <p className="text-sm text-gray-500 mb-4">{t('purchaseOrders.forms.startUnloadingForm.subtitle')}</p>
                    

                    {/* Items List */}
                    {adviceData && adviceData.items && adviceData.items.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                <FontAwesomeIcon icon={faBoxOpen} className="text-xs" />
                                Items to Unload
                            </h3>
                            <div className="space-y-3">
                                {adviceData.items.map((item, idx) => (
                                    <div key={item.id} className="p-3 rounded-xl border bg-gray-50/50 flex flex-col gap-2" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="font-bold text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{item.size_name || item.product_name}</p>
                                                    {item.load_by_pieces && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-teal-50 text-teal-700 border border-teal-200">{t('common.formFields.labels.loadByPieces')}</span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500">{item.quantity} {item.unit}</p>
                                            </div>
                                            {item.quantity_in_pieces && (
                                                <div className="text-right">
                                                    <p className="text-lg font-bold text-blue-600 leading-none">{item.quantity_in_pieces}</p>
                                                    <p className="text-[10px] font-bold uppercase text-gray-400 tracking-tighter">{t('common.formFields.labels.pieces')}</p>
                                                </div>
                                            )}
                                        </div>
                                        {item.remark && (
                                            <div className="mt-1">
                                                <div 
                                                    className="mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-100 text-yellow-800 border border-yellow-200 uppercase tracking-tight"
                                                    style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap', maxWidth: '180px' }}
                                                >
                                                    {item.remark}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {(poNumber || adviceNumber) && (
                            <div className="p-4 rounded-xl mb-4 space-y-2" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.05), border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1)}` }}>
                                {poNumber && (
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">{t('purchaseOrders.forms.startUnloadingForm.fields.poNumber')}</span>
                                        <span className="font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{poNumber}</span>
                                    </div>
                                )}
                                {adviceNumber && (
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">{t('purchaseOrders.forms.startUnloadingForm.fields.adviceNumber')}</span>
                                        <span className="font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{adviceNumber}</span>
                                    </div>
                                )}
                            </div>
                        )}



                        <div>
                            <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrders.forms.startUnloadingForm.fields.finalWeight.label')} <span>*</span></label>
                            <input
                                type="number"
                                name="loaded_truck_weight"
                                value={formData.loaded_truck_weight}
                                onChange={handleChange}
                                onWheel={(e) => e.target.blur()}
                                placeholder={t('purchaseOrders.forms.startUnloadingForm.fields.finalWeight.placeholder')}
                                className="w-full px-4 py-3 rounded-xl border-2 transition-all outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:border-blue-500"
                                style={{ borderColor: errors.loaded_truck_weight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                            />
                                {errors.loaded_truck_weight && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.loaded_truck_weight}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrders.forms.startUnloadingForm.fields.remark.label')}</label>
                            <textarea
                                name="remark"
                                value={formData.remark}
                                onChange={handleChange}
                                placeholder={t('purchaseOrders.forms.startUnloadingForm.fields.remark.placeholder')}
                                className="w-full px-4 py-3 rounded-xl border-2 transition-all outline-none resize-none h-24 focus:border-blue-500"
                                style={{ borderColor: UI_COLORS.FORMS.INPUT.border }}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.internalRemarks')}</label>
                            <textarea
                                name="internal_remark"
                                value={formData.internal_remark}
                                onChange={handleChange}
                                placeholder={t('common.formFields.placeholders.internalRemarksOptional')}
                                className="w-full px-4 py-3 rounded-xl border-2 transition-all outline-none resize-none h-16 focus:border-blue-500"
                                style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac' }}
                            />
                        </div>

                        <div className="flex justify-end gap-4 pt-4">
                            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl font-medium transition-colors" style={{ backgroundColor: '#F3F4F6', color: '#374151' }}>{t('common.buttons.cancel')}</button>
                            <button type="submit" disabled={loading || fetching} className="px-6 py-3 rounded-xl font-semibold text-white shadow-lg transition-all hover:shadow-xl active:scale-95 disabled:opacity-50" style={{ backgroundColor: UI_COLORS.BRAND.PRIMARY, color: UI_COLORS.TEXT.PRIMARY }}>
                                <FontAwesomeIcon icon={faTruckLoading} className="mr-2" />
                                {t('purchaseOrders.forms.startUnloadingForm.buttons.start')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default StartUnloadingForm;

